/**
 * Вкладка «AI» правой панели «Чатов»: хронология кнопки AI по чату — каждый черновик (тип обращения,
 * линия и версия сценария, итог, цена) и замечания AI Lab к нему с разбором (статус, что изменили, версия).
 * Свежие сверху, по сообщениям клиента: видно, сколько раз ответ переписывали и что менялось.
 * Спека `docs/specs/2026-11-03-chat-ai-button-ux-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, Sparkles } from "lucide-react";
import { Link } from "react-router";

import { type AiHistoryDraft, aiHistoryQuery, lineLabel, money } from "@/lib/chatSuggest";

const ЦВЕТ: Record<string, string> = {
  sent: "text-ok",
  rejected: "text-err",
  new: "text-accent",
};

/** «chat/zapis/v2:80ca4d62» → ссылка на линию во вкладке «Сценарии» AI Lab. */
function scenarioLink(label: string): string | null {
  if (!label.includes("/v")) return null;
  const line = label.split("/v")[0];
  return `/voice-lab?tab=scenarios&line=${encodeURIComponent(line ?? "")}`;
}

function Draft({ d, rate, n }: { d: AiHistoryDraft; rate: number; n: number }) {
  const link = scenarioLink(d.label ?? "");
  return (
    <li className="flex flex-col gap-1 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-2">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[12px] text-fg-muted">
        <span className="font-medium text-fg">№ {n}</span>
        <span>{d.at?.slice(8, 10)}.{d.at?.slice(5, 7)} {d.at?.slice(11, 16)}</span>
        <span className={`font-medium ${ЦВЕТ[d.state] ?? ""}`}>{d.state_label}</span>
        {d.user && <span>· {d.user}</span>}
        <span className="ml-auto tabular-nums" title={`Черновик ${money(d.cost ?? 0)}, детектор ${money(d.detect_cost ?? 0)}`}>
          {money(d.total_cost, rate)}
        </span>
      </div>
      {d.scenario && (
        <div className="text-[12px]">
          <span className="font-medium">{d.scenario.label}</span>
          <span className="text-fg-muted">
            {d.scenario.manual
              ? " · выбран вручную"
              : d.scenario.confidence != null
                ? ` · ${Math.round(d.scenario.confidence * 100)}%`
                : ""}
          </span>
        </div>
      )}
      <div className="text-[11.5px] text-fg-muted">
        {link ? (
          <Link to={link} className="text-accent hover:underline" title="Линия и версии сценария в AI Lab">
            {lineLabel(d.label ?? "")}
          </Link>
        ) : (
          lineLabel(d.label ?? "")
        )}
      </div>
      {d.text ? (
        <p className="whitespace-pre-wrap break-words text-[13px] leading-[18px]">{d.text}</p>
      ) : (
        <p className="text-[12px] text-err">{d.error || "—"}</p>
      )}
      {d.why && <p className="text-[12px] text-fg-muted">{d.why}</p>}
      {d.remarks.map((r) => (
        <div key={r.id} className="rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px]" data-annot="замечание к черновику AI">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">Замечание AI Lab #{r.id}</span>
            <span className="text-fg-muted">{r.status_label}</span>
            <Link
              to={`/voice-lab?tab=remarks&status=all&channel=chat&remark=${r.id}`}
              className="ml-auto text-accent hover:underline"
              title="Это замечание в AI Lab: статус, ответ, версия сценария"
            >
              открыть в AI Lab →
            </Link>
          </div>
          <div className="whitespace-pre-wrap break-words">
            {[r.text, r.transcript].filter(Boolean).join("\n") ||
              (r.status === "voice_failed" ? "голос не расшифровался" : "голос расшифровывается…")}
          </div>
          {r.answer?.change && (
            <div className="mt-0.5 text-fg-muted">
              Что изменили: {r.answer.change}
              {r.answer.version ? ` · ${r.answer.version}` : ""}
            </div>
          )}
        </div>
      ))}
    </li>
  );
}

export function ChatAiHistory({ chatId }: { chatId: number }) {
  const q = useQuery({ ...aiHistoryQuery(chatId), refetchInterval: 15_000 });
  if (q.isPending) return <p className="p-3 text-fg-muted">Загружаю…</p>;
  if (q.isError) return <p className="p-3 text-err">История AI не загрузилась: {q.error.message}</p>;
  const h = q.data;
  if (!h.drafts.length)
    return (
      <p className="flex items-center gap-1 p-3 text-fg-muted">
        <Sparkles size={14} /> Кнопку AI в этом чате ещё не нажимали
      </p>
    );
  // По сообщениям клиента: на одно сообщение — несколько попыток (переписать, сменить тип).
  const групп: { question: string; message_id: number; drafts: AiHistoryDraft[] }[] = [];
  for (const d of h.drafts) {
    const g = групп.find((x) => x.message_id === d.message_id);
    if (g) g.drafts.push(d);
    else групп.push({ question: d.question, message_id: d.message_id, drafts: [d] });
  }
  return (
    <div className="flex flex-col gap-3 p-3" data-annot="история кнопки AI">
      <div className="text-[12.5px] text-fg-muted">
        Черновиков: {h.count} · потрачено {money(h.cost, h.rate)}
        <span title={h.rate_source}> · курс {h.rate.toFixed(2)} ₽</span>
      </div>
      {групп.map((g) => (
        <section key={g.message_id} className="flex flex-col gap-1.5">
          <div className="flex items-start gap-1 text-[12.5px]">
            <MessageSquare size={14} className="mt-0.5 flex-none text-fg-muted" />
            <span className="min-w-0">
              <span className="text-fg-muted">Клиент: </span>«{g.question}»
              <span className="text-fg-muted"> · попыток {g.drafts.length}</span>
            </span>
          </div>
          <ol className="flex flex-col gap-1.5">
            {g.drafts.map((d, i) => (
              <Draft key={d.id} d={d} rate={h.rate} n={g.drafts.length - i} />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
